import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, ButtonGroup, ToggleButton, Spinner } from "react-bootstrap";
import { FaCheck, FaEdit, FaTrash } from 'react-icons/fa';
import { IoCloseSharp } from 'react-icons/io5';
import AppPagination from '../../Components/AppPagination';
import toast from 'react-hot-toast';
import { getOrders, updateOrder, deleteOrder } from '../../Services/orderService';

const ConfirmOrders = () => {

    const [orders, setOrders] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);

    const [filters, setFilters] = useState({
        is_confirmed: 'all',
        from: '',
        to: '',
        trackingId: ''
    });

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [remark, setRemark] = useState("");

    const [showEditModal, setShowEditModal] = useState(false);
    const [editData, setEditData] = useState({});

    const formatDateForAPI = (date, isEnd = false) => {
        if (!date) return "";   // undefined nahi, empty string

        const d = new Date(date);

        if (isEnd) {
            d.setHours(23, 59, 59, 999);
        } else {
            d.setHours(0, 0, 0, 0);
        }

        return d.toISOString();
    };

    const fetchOrders = async () => {
        try {
            setLoading(true);

            const res = await getOrders({
                page,
                limit: 50,   // ✅ frontend se hard-coded limit
                is_confirmed: filters.is_confirmed === "all"
                    ? ""        // empty string means fetch all
                    : filters.is_confirmed,
                from: formatDateForAPI(filters.from),
                to: formatDateForAPI(filters.to, true),
                trackingId: filters.trackingId || ""
            });

            if (res?.status === "success") {
                setOrders(res.data);
                setTotalPages(res.totalPages);
            }

        } catch (error) {
            toast.error("Failed to fetch orders");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delay = setTimeout(() => {
            fetchOrders();
        }, 400);

        return () => clearTimeout(delay);
    }, [page, filters]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setPage(1);
    };

    // ✅ Confirm
    const handleConfirm = async (orderId) => {
        try {
            setLoading(true);
            await updateOrder(orderId, { is_confirmed: "confirmed" });
            toast.success("Order confirmed");
            await fetchOrders();
        } catch {
            toast.error("Failed to confirm order");
        } finally {
            setLoading(false);
        }
    };

    // ✅ Reject
    const handleReject = (order) => {
        setSelectedOrder(order);
        setRemark(order.remark || "");
        setShowRejectModal(true);
    };

    const handleRejectSubmit = async () => {
        try {
            setLoading(true);
            await updateOrder(selectedOrder.id, { is_confirmed: "rejected", remark });
            toast.success("Order rejected");
            setShowRejectModal(false);
            await fetchOrders();
        } catch {
            toast.error("Failed to reject order");
        } finally {
            setLoading(false);
        }
    };

    // ✅ Edit
    const handleEdit = (order) => {
        setSelectedOrder(order);
        setEditData({ ...order });
        setShowEditModal(true);
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditData(prev => ({ ...prev, [name]: value }));
    };

    const handleEditSubmit = async () => {
        try {
            setLoading(true);
            await updateOrder(selectedOrder.id, editData);
            toast.success("Order updated");
            setShowEditModal(false);
            await fetchOrders();
        } catch {
            toast.error("Failed to update order");
        } finally {
            setLoading(false);
        }
    };

    // ✅ Delete
    const handleDelete = async (orderId) => {
        if (!window.confirm("Are you sure you want to delete this order?")) return;

        try {
            setLoading(true);
            await deleteOrder(orderId);
            toast.success("Order deleted successfully");
            await fetchOrders();
        } catch {
            toast.error("Failed to delete order");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container pb-5">
            <h4 className="mb-3 fw-semibold">Orders Confirmation</h4>

            {/* Filters */}
            <div className="row g-2 mb-3 justify-content-end align-items-end">

                <div className="col-auto">
                    <Form.Label>From</Form.Label>
                    <Form.Control type="date" name="from" value={filters.from} onChange={handleFilterChange} />
                </div>

                <div className="col-auto">
                    <Form.Label>To</Form.Label>
                    <Form.Control type="date" name="to" value={filters.to} onChange={handleFilterChange} />
                </div>

                <div className="col-auto">
                    <Form.Label>Status</Form.Label>
                    <br />
                    <ButtonGroup>
                        {["all", "confirmed", "rejected"].map(status => (
                            <ToggleButton
                                key={status}
                                type="radio"
                                className="text-capitalize"
                                variant={
                                    filters.is_confirmed === status
                                        ? "dark"
                                        : "outline-dark"
                                }
                                checked={filters.is_confirmed === status}
                                value={status}
                                onClick={() => {
                                    setFilters(prev => ({
                                        ...prev,
                                        is_confirmed: status
                                    }));
                                    setPage(1);
                                }}
                                disabled={loading}
                            >
                                {status}
                            </ToggleButton>
                        ))}
                    </ButtonGroup>
                </div>

                <div className="col-auto">
                    <Form.Label>Search</Form.Label>
                    <Form.Control
                        type="text"
                        name="trackingId"
                        placeholder="Tracking ID"
                        value={filters.trackingId}
                        onChange={handleFilterChange}
                        disabled={loading}
                    />
                </div>

            </div>

            {/* Table */}
            <Table bordered responsive>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Order ID</th>
                        <th>VPP</th>
                        <th>Name</th>
                        <th>Contact</th>
                        <th>City</th>
                        <th>Product</th>
                        <th>Qty</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Action</th>
                    </tr>
                </thead>

                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan="11" className="text-center py-4">
                                <Spinner animation="border" variant="dark" />
                            </td>
                        </tr>
                    ) : orders.length === 0 ? (
                        <tr>
                            <td colSpan="11" className="text-center py-4">
                                No orders found
                            </td>
                        </tr>
                    ) : (
                        orders.map((order, idx) => (
                            <tr key={order.id} className={order.is_duplicate ? "table-danger" : ""}>
                                <td>{idx + 1 + (page - 1) * 50}</td>
                                <td>{order.trackingId}</td>
                                <td>{order.VppNumber || "-"}</td>
                                <td>{order.customerName}</td>
                                <td>{order.phoneNumber}</td>
                                <td>{order.city}</td>
                                <td>{order.product || "-"}</td>
                                <td>{order.quantity}</td>
                                <td>{order.amount}</td>
                                <td>
                                    <span className={`badge text-capitalize ${order.is_confirmed === "confirmed" ? "bg-success" : order.is_confirmed === "rejected" ? "bg-danger" : "bg-secondary"}`}>
                                        {order.is_confirmed || "pending"}
                                    </span>
                                </td>
                                <td>
                                    {order.is_confirmed === "rejected" ? (
                                        <span className="text-danger fw-semibold">
                                            {order.remark || "Rejected"}
                                        </span>
                                    ) : (
                                        <>
                                            <Button size="sm" variant="success" className="me-2" disabled={loading} onClick={() => handleConfirm(order.id)}>
                                                <FaCheck />
                                            </Button>

                                            <Button size="sm" variant="danger" className="me-2" disabled={loading} onClick={() => handleReject(order)}>
                                                <IoCloseSharp />
                                            </Button>

                                            <Button size="sm" variant="primary" className="me-2" disabled={loading} onClick={() => handleEdit(order)}>
                                                <FaEdit />
                                            </Button>

                                            <Button size="sm" variant="dark" disabled={loading} onClick={() => handleDelete(order.id)}>
                                                <FaTrash />
                                            </Button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>

            <AppPagination page={page} totalPages={totalPages} onPageChange={setPage} />

            {/* Reject Modal */}
            <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Reject Order</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Control
                        as="textarea"
                        rows={4}
                        value={remark}
                        onChange={e => setRemark(e.target.value)}
                        placeholder="Enter rejection reason"
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRejectModal(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleRejectSubmit} disabled={loading}>
                        {loading ? <Spinner size="sm" animation="border" variant="light" /> : "Submit"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Order: {selectedOrder?.trackingId}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control name="customerName" value={editData.customerName || ""} onChange={handleEditChange} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control type="number" name="phoneNumber" value={editData.phoneNumber || ""} onChange={handleEditChange} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Address</Form.Label>
                            <Form.Control name="address" value={editData.address || ""} onChange={handleEditChange} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Quantity</Form.Label>
                            <Form.Control type="number" name="quantity" value={editData.quantity || 1} onChange={handleEditChange} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Amount</Form.Label>
                            <Form.Control type="number" name="amount" value={editData.amount || ""} onChange={handleEditChange} />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>VPP Number</Form.Label>
                            <Form.Control maxLength={11} name="VppNumber" value={editData.VppNumber || ""} onChange={handleEditChange} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleEditSubmit} disabled={loading}>
                        {loading ? <Spinner size="sm" animation="border" variant="light" /> : "Save Changes"}
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default ConfirmOrders;
